import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calculator, ArrowRight, X } from 'lucide-react';

interface FloatingCTAProps {
    text?: string;
    link?: string;
    icon?: React.ReactNode;
    showAfterPercent?: number;
}

const FloatingCTA: React.FC<FloatingCTAProps> = ({
    text = "Usar Calculadoras",
    link = "/calculadoras",
    icon = <Calculator className="w-5 h-5" />,
    showAfterPercent = 30
}) => {
    const [isVisible, setIsVisible] = useState(false);
    const [isDismissed, setIsDismissed] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            const scrollPercent = (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100;

            if (scrollPercent > showAfterPercent && !isDismissed) {
                setIsVisible(true);
            } else if (scrollPercent <= showAfterPercent) {
                setIsVisible(false);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [showAfterPercent, isDismissed]);

    const handleDismiss = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDismissed(true);
        setIsVisible(false);
    };

    if (!isVisible) return null;

    return (
        <div className="fixed bottom-6 right-6 z-40 animate-slide-up">
            <div className="relative group">
                <button
                    onClick={handleDismiss}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-gray-800 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-gray-700"
                    aria-label="Fechar"
                >
                    <X className="w-4 h-4" />
                </button>
                <Link
                    to={link}
                    className="flex items-center gap-3 px-6 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-2xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
                >
                    <span className="p-2 bg-white/20 rounded-lg">
                        {icon}
                    </span>
                    <span className="font-semibold">{text}</span>
                    <ArrowRight className="w-5 h-5" />
                </Link>
            </div>

            <style>{`
                @keyframes slide-up {
                    from {
                        opacity: 0;
                        transform: translateY(20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                .animate-slide-up {
                    animation: slide-up 0.3s ease-out forwards;
                }
            `}</style>
        </div>
    );
};

export default FloatingCTA;
